import React, { useEffect } from 'react';
import styles from './TradeRecommendationList.module.css';
import TradeRecommendationTable from '../TradeRecommendationTable/TradeRecommendationTable';
import TradeRecommendationCard from '../TradeRecommendationCard/TradeRecommendationCard';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import Spinner from '../../common/Spinner/Spinner';
import { usePermission } from '../../../hooks/usePermissions';
import Select from 'react-select';

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const customSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '36px',
        height: '36px',
        borderRadius: '8px',
        border: state.isFocused ? '1px solid var(--color-primary)' : '1px solid var(--border-medium)',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(33, 15, 71, 0.08)' : 'none',
        fontSize: '13px',
        cursor: 'pointer',
        '&:hover': {
            border: state.isFocused ? '1px solid var(--color-primary)' : '1px solid var(--text-muted)'
        }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    menu: (base) => ({ ...base, zIndex: 9999, fontSize: '13px', borderRadius: '8px', overflow: 'hidden' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? 'var(--color-primary)' : state.isFocused ? 'var(--bg-gray)' : 'white',
        color: state.isSelected ? 'white' : 'var(--text-body)',
        cursor: 'pointer',
        '&:active': {
            backgroundColor: 'var(--color-primary)',
            color: 'white'
        }
    })
};

const TradeRecommendationList = ({
    tradeRecommendations = [],
    total = 0,
    loading = false,
    onEdit,
    onDelete,
    onView,
    onFilterChange,        // new prop
    currentFilters = {},
    onRefresh,
    onAddRecommendation,
    scriptTypes = [],
    loadingTypes = false,
    statusOptions = [],
    loadingStatusOptions = false,
    hideUserIdFilter = false,
}) => {
    const { hasPermission } = usePermission()

    const filteredTradeRecommendations = tradeRecommendations;

    if (loading && tradeRecommendations.length === 0) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner size="large" text="Loading trade recommendations..." />
            </div>
        );
    }

    return (
        <div className={styles.tradeListContainer}>
            <div className={styles.stickyHeaderWrapper}>

                {/* FILTERS SECTION */}
                <div className={styles.filtersSection}>
                    {/* ROW 1 */}
                    <div className={styles.filtersRow}>
                        {/* Search Field */}
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>Search</label>
                            <div className={styles.searchSection}>
                                <div className={styles.imSearchWrap}>
                                    <SearchIcon />
                                    <input
                                        type="text"
                                        placeholder="Search script..."
                                        value={currentFilters.searchTerm || ''}
                                        onChange={(e) => onFilterChange({ ...currentFilters, searchTerm: e.target.value })}
                                    />
                                </div>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        onFilterChange({
                                            ...currentFilters,
                                            status: '',
                                            type: '',
                                            createdFrom: '',
                                            createdTo: '',
                                            exitFrom: '',
                                            exitTo: '',
                                            searchTerm: '',
                                            userId: '',
                                            trackOnly: '',
                                        });
                                    }}
                                    className={styles.clearFiltersButton}
                                >
                                    Clear
                                </Button>
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div className={`${styles.filterGroup} ${styles.filterGroupMinWidth160}`}>
                            <label className={styles.filterLabel}>Status</label>
                            <Select
                                value={currentFilters.status ? { value: currentFilters.status, label: currentFilters.status } : null}
                                onChange={(selected) => onFilterChange({ ...currentFilters, status: selected ? selected.value : '' })}
                                options={[
                                    { value: "", label: "All" },
                                    ...(statusOptions.length > 0 ? statusOptions : [
                                        { value: "Live", label: "Live" },
                                        { value: "Closed", label: "Closed" },
                                        { value: "Target Hit", label: "Target Hit" },
                                        { value: "Stoploss Hit", label: "Stoploss Hit" },
                                    ])
                                ]}
                                placeholder="All"
                                isClearable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                components={{ IndicatorSeparator: () => null }}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '36px', height: '36px', borderRadius: '8px', border: '1px solid var(--border-medium)', fontSize: '13px' }),
                                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: '13px' }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                                isDisabled={loadingStatusOptions}
                            />
                        </div>

                        {/* Type Filter */}
                        <div className={`${styles.filterGroup} ${styles.filterGroupMinWidth160}`}>
                            <label className={styles.filterLabel}>Type</label>
                            <Select
                                value={currentFilters.type ? { value: currentFilters.type, label: currentFilters.type } : null}
                                onChange={(selected) => onFilterChange({ ...currentFilters, type: selected ? selected.value : '' })}
                                options={[
                                    { value: "", label: "All" },
                                    ...scriptTypes.map(type => ({ value: type.scriptTypeName, label: type.scriptTypeName }))
                                ]}
                                placeholder="All"
                                isClearable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                components={{ IndicatorSeparator: () => null }}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '36px', height: '36px', borderRadius: '8px', border: '1px solid var(--border-medium)', fontSize: '13px' }),
                                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999, fontSize: '13px' }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                                isDisabled={loadingTypes}
                            />
                        </div>

                        {/* Created Date Range */}
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>Created</label>
                            <div className={styles.dateRange}>
                                <Input
                                    type="date"
                                    value={currentFilters.createdFrom || ''}
                                    onChange={(e) => onFilterChange({ ...currentFilters, createdFrom: e.target.value })}
                                    className={styles.dateInput}
                                    size="small"
                                />
                                <Input
                                    type="date"
                                    value={currentFilters.createdTo || ''}
                                    onChange={(e) => onFilterChange({ ...currentFilters, createdTo: e.target.value })}
                                    className={styles.dateInput}
                                    size="small"
                                />
                            </div>
                        </div>

                        {/* Exit Date Range */}
                        <div className={styles.filterGroup}>
                            <label className={styles.filterLabel}>Exit</label>
                            <div className={styles.dateRange}>
                                <Input
                                    type="date"
                                    value={currentFilters.exitFrom || ''}
                                    onChange={(e) => onFilterChange({ ...currentFilters, exitFrom: e.target.value })}
                                    className={styles.dateInput}
                                    size="small"
                                />
                                <Input
                                    type="date"
                                    value={currentFilters.exitTo || ''}
                                    onChange={(e) => onFilterChange({ ...currentFilters, exitTo: e.target.value })}
                                    className={styles.dateInput}
                                    size="small"
                                />
                            </div>
                        </div>
                    </div>

                    {/* ROW 2 */}
                    <div className={styles.filtersRow}>
                        {/* User ID Filter */}
                        {!hideUserIdFilter && (
                            <div className={`${styles.filterGroup} ${styles.userIdGroup} ${styles.userIdFilterGroup}`}>
                                <label className={styles.filterLabel}>User ID</label>
                                <Input
                                    placeholder="User ID..."
                                    value={currentFilters.userId || ''}
                                    onChange={(e) => onFilterChange({ ...currentFilters, userId: e.target.value })}
                                    size="small"
                                    className={styles.headerSearch}
                                />
                            </div>
                        )}
                        {/* Track Only Filter */}
                        <div className={`${styles.filterGroup} ${styles.trackOnlyFilterGroup}`}>
                            <label className={styles.filterLabel}>Track Only</label>
                            <Select
                                value={currentFilters.trackOnly !== undefined && currentFilters.trackOnly !== '' ? { value: currentFilters.trackOnly, label: currentFilters.trackOnly === 'true' ? 'Yes' : 'No' } : null}
                                onChange={(selected) => onFilterChange({ ...currentFilters, trackOnly: selected ? selected.value : '' })}
                                options={[
                                    { value: "", label: "All" },
                                    { value: "true", label: "Yes" },
                                    { value: "false", label: "No" },
                                ]}
                                placeholder="All"
                                isClearable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                components={{ IndicatorSeparator: () => null }}
                                styles={customSelectStyles}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {loading && tradeRecommendations.length > 0 && (
                <div className={styles.loadingOverlay}>
                    <Spinner text="Updating..." />
                </div>
            )}

            <div className={styles.bkTableWrap}>
                <div className={styles.tradeListContent}>
                    {filteredTradeRecommendations.length === 0 ? (
                        <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>📊</div>
                        <h3 className={styles.emptyTitle}>
                            {currentFilters.searchTerm ? 'No recommendations found' : 'No trade recommendations available'}
                        </h3>
                        <p className={styles.emptyMessage}>
                            {currentFilters.searchTerm
                                ? `No recommendations match "${currentFilters.searchTerm}". Try adjusting your search terms.`
                                : 'There are no trade recommendations to display at the moment.'
                            }
                        </p>
                        {currentFilters.searchTerm && (
                            <Button variant="outline" onClick={() => onFilterChange({ ...currentFilters, searchTerm: '' })}>
                                Clear Search
                            </Button>
                        )}
                    </div>
                ) : (
                    <>
                        <TradeRecommendationTable
                            tradeRecommendations={filteredTradeRecommendations}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onView={onView}
                            canEdit={hasPermission("MODIFY_TRADERECOMMENDATIONS")}
                            canCreate={hasPermission("CREATE_TRADERECOMMENDATIONS")}
                            canDelete={hasPermission("DELETE_TRADERECOMMENDATIONS")}
                        />
                    </>
                )}
            </div>
            </div>
        </div>
    );
};

export default TradeRecommendationList;